import { DASH, LEAP } from "@eldrun/content";
import { dcos, dsin, wrapAngle } from "@eldrun/shared";
import { EAT, MOVE, STAMINA } from "./movement-constants.ts";
import { boxTopAt, type Box } from "./building.ts";
import { SUB_STEP_SECONDS } from "./timestep.ts";

/**
 * Estado simulado do jogador. Só números e booleanos: é espelhado 1:1 pelo schema (`Avatar`)
 * e restaurado pelo reconciler do cliente. Todos os floats ficam representáveis em float32
 * ao fim de cada passo (ver `roundState`), então o que vai pela rede é exatamente o estado.
 */
export interface MoveState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  yaw: number;
  pitch: number;
  /**
   * Direção da mira (F55): do olho ao ponto sob a cruz da câmera em terceira pessoa, aceita só até
   * `AIM.maxDeviation` da câmera (`yaw`/`pitch`); senão, igual à câmera. Golpe e flecha saem por ela.
   */
  aimYaw: number;
  aimPitch: number;
  grounded: boolean;
  crouching: boolean;
  swimming: boolean;
  jumpHeld: boolean;
  crouchHeld: boolean;
  coyote: number;
  jumpBuffer: number;
  jumpCooldown: number;
  slideTime: number;
  slideCooldown: number;
  /** Investida (F66): tempo restante do avanço, recarga, botão mantido e a velocidade (0 = sem a habilidade; o servidor põe). */
  dashTime: number;
  dashCooldown: number;
  dashHeld: boolean;
  dashPower: number;
  /** Salto do Falcão (F67): no ar pelo salto, botão mantido e a velocidade (0 = sem a ultimate carregada; o servidor põe). */
  leaping: boolean;
  leapHeld: boolean;
  leapPower: number;
  airJumps: number;
  /** Multiplicador externo de velocidade (Simpatias, travessuras, Mandinga). */
  speedMul: number;
  /** Multiplicador de gravidade (Mandinga "Pé de Vento"). */
  gravityMul: number;
  /** Saltos aéreos extras disponíveis por salto do chão (Pena de Gavião). */
  maxAirJumps: number;
  safeX: number;
  safeY: number;
  safeZ: number;
  /** Fôlego atual (0…staminaMax). */
  stamina: number;
  /** Máximo de Fôlego (externo: Simpatias). */
  staminaMax: number;
  /** Segundos desde o último gasto de Fôlego. */
  staminaIdle: number;
  /** Zerou o Fôlego: sem correr nem deslizar até recuperar `STAMINA.exhaustedUntil`. */
  exhausted: boolean;
  /** Multiplicador externo da recuperação de Fôlego (prato "Fôlego"). */
  staminaRegenMul: number;
  /** Fome zerada (externo, decidido pelo servidor): Fôlego recupera a 40 %. */
  starving: boolean;
  /** Olhos abaixo do nível do mar (mergulho): gasta Fôlego e, sem ele, afoga. */
  submerged: boolean;
  /** O item na mão é comestível (externo, decidido pelo servidor). */
  canEat: boolean;
  /** Segundos de canal de comer com a ação mantida. */
  useTime: number;
  /** Mordidas completas (contador de 16 bits): o servidor aplica a comida quando muda. */
  eatCount: number;
  /** Caído (externo, decidido pelo servidor): ignora o comando e cancela o que estava pendente. */
  downed: boolean;
  /** Multiplicador externo só da corrida (conjunto completo de Couro, +15 %). */
  sprintMul: number;
  /** Controles invertidos (externo, decidido pelo servidor: travessura do Saci). */
  inverted: boolean;
  /** Carregado pela Jangada (externo, decidido pelo servidor): o passo não mexe no corpo. */
  carried: boolean;
}

/** Entrada de um passo fixo (espelha o schema `MoveInput`). */
export interface MoveCommand {
  /** −1 esquerda, 1 direita. */
  moveX: number;
  /** −1 trás, 1 frente. */
  moveZ: number;
  yaw: number;
  pitch: number;
  /**
   * Direção do olho até o ponto sob a cruz (F55; doc 02 §6). Sem ela (comando ocioso do servidor,
   * testes), a mira é a própria câmera.
   */
  aimYaw?: number;
  aimPitch?: number;
  jump: boolean;
  sprint: boolean;
  crouch: boolean;
  /** Ação principal mantida (comer, com comida na mão). */
  use: boolean;
  /** Habilidade ativa mantida (F66): a Investida, para quem a tem (`dashPower` > 0). */
  dash?: boolean;
  /** Ultimate mantida (F67): o Salto do Falcão, para quem o tem carregado (`leapPower` > 0). */
  leap?: boolean;
  /**
   * Ponto mirado do Salto (F97), no mundo, em ±`LEAP_AIM_LIMIT`. Sem ele (ou não finito), o Salto vai para a
   * frente, no alcance todo; pela rede ele sempre chega (0 por padrão).
   */
  leapX?: number;
  leapZ?: number;
}

/** Cilindro vertical estático (tronco, rocha) para colisão. */
export interface Cylinder {
  x: number;
  z: number;
  radius: number;
  bottom: number;
  top: number;
}

/** O que a simulação precisa do mundo. Implementado sobre o heightfield (servidor e cliente). */
export interface SimWorld {
  seaLevel: number;
  height(x: number, z: number): number;
  /** Gradiente do terreno (dh/dx, dh/dz) no triângulo sob (x, z). */
  gradient(x: number, z: number, out: { x: number; z: number }): { x: number; z: number };
  /** Cilindros perto de (x, z), acumulados em `out`. */
  cylinders(x: number, z: number, radius: number, out: Cylinder[]): Cylinder[];
  /** Caixas das construções perto de (x, z) (F17); mundos sem construção não implementam. */
  boxes?(x: number, z: number, radius: number, out: Box[]): Box[];
}

const boxScratch: Box[] = [];

/**
 * Chão sob (x, z) para quem está na altura `y`: o terreno, ou o topo de piso, rampa, telhado ou
 * alicerce que não esteja acima de um degrau (topos mais altos são parede, não chão). `onCeiling`: o
 * chão é um teto suspenso (a laje de pedra da Caverna).
 */
export function groundAt(world: SimWorld, x: number, z: number, y: number): { height: number; onBox: boolean; onCeiling: boolean } {
  let height = world.height(x, z);
  let onBox = false;
  let onCeiling = false;
  if (!world.boxes) return { height, onBox, onCeiling };
  boxScratch.length = 0;
  for (const b of world.boxes(x, z, 0.1, boxScratch)) {
    if (x < b.minX || x > b.maxX || z < b.minZ || z > b.maxZ) continue;
    const top = boxTopAt(b, x, z);
    if (top > y + MOVE.stepHeight || top <= height) continue;
    height = top;
    onBox = true;
    onCeiling = b.ceiling === true;
  }
  boxScratch.length = 0;
  return { height, onBox, onCeiling };
}

export function createMoveState(x = 0, y = 0, z = 0): MoveState {
  return {
    x,
    y,
    z,
    vx: 0,
    vy: 0,
    vz: 0,
    yaw: 0,
    pitch: 0,
    aimYaw: 0,
    aimPitch: 0,
    grounded: false,
    crouching: false,
    swimming: false,
    jumpHeld: false,
    crouchHeld: false,
    coyote: 0,
    jumpBuffer: 0,
    jumpCooldown: 0,
    slideTime: 0,
    slideCooldown: 0,
    dashTime: 0,
    dashCooldown: 0,
    dashHeld: false,
    dashPower: 0,
    leaping: false,
    leapHeld: false,
    leapPower: 0,
    airJumps: 0,
    speedMul: 1,
    gravityMul: 1,
    maxAirJumps: 0,
    safeX: x,
    safeY: y,
    safeZ: z,
    stamina: STAMINA.max,
    staminaMax: STAMINA.max,
    staminaIdle: 0,
    exhausted: false,
    staminaRegenMul: 1,
    starving: false,
    submerged: false,
    canEat: false,
    useTime: 0,
    eatCount: 0,
    downed: false,
    sprintMul: 1,
    inverted: false,
    carried: false,
  };
}

const FLOAT_FIELDS = [
  "x", "y", "z", "vx", "vy", "vz", "yaw", "pitch", "aimYaw", "aimPitch", "coyote", "jumpBuffer", "jumpCooldown",
  "slideTime", "slideCooldown", "dashTime", "dashCooldown", "dashPower", "leapPower", "speedMul", "gravityMul", "safeX", "safeY", "safeZ",
  "stamina", "staminaMax", "staminaIdle", "staminaRegenMul", "useTime", "sprintMul",
] as const satisfies readonly (keyof MoveState)[];

/** Arredonda todos os floats a float32: o estado é sempre o que a rede transporta. */
export function roundState(s: MoveState): void {
  for (const f of FLOAT_FIELDS) s[f] = Math.fround(s[f]);
}

/** ±89°, em float32. */
const PITCH_LIMIT = Math.fround(1.5533430342749532);

/**
 * Mira no ponto sob a cruz (F55; doc 02 §6): o desvio máximo aceito entre a direção do olho e a da
 * câmera, e o cosseno dele (literal: `Math.cos` fica fora da simulação, doc 07 A2).
 */
export const AIM = { maxDeviation: (25 * Math.PI) / 180, cosMaxDeviation: 0.9063077870366499 } as const;

const clampPitch = (pitch: number): number => (pitch > PITCH_LIMIT ? PITCH_LIMIT : pitch < -PITCH_LIMIT ? -PITCH_LIMIT : pitch);

/** Cosseno do ângulo entre as direções (yaw, pitch) `a` e `b` (1 = iguais). */
export function aimCos(yawA: number, pitchA: number, yawB: number, pitchB: number): number {
  return dcos(pitchA) * dcos(pitchB) * dcos(yawA - yawB) + dsin(pitchA) * dsin(pitchB);
}

/**
 * Olhar do comando no estado: câmera (`yaw`/`pitch`, como chegam pela rede, em float32) e mira. A mira
 * do comando vale se for finita e estiver a até 25° da câmera; senão (ausente, adulterada, fora do
 * cone), a mira é a própria câmera. Igual no servidor e na predição.
 */
function applyLook(s: MoveState, cmd: MoveCommand): void {
  // Câmera não finita (NaN, ±∞ ou ausente — o NaN chega da rede como `null`) não entra: fica a do
  // estado. A faixa do `sanitize` da sala não pega NaN, e um yaw NaN seguia até a lápide e a trouxa
  // na morte (coordenada não finita: a sala caía).
  if (Number.isFinite(cmd.yaw)) s.yaw = Math.fround(wrapAngle(Math.fround(cmd.yaw)));
  if (Number.isFinite(cmd.pitch)) s.pitch = clampPitch(Math.fround(cmd.pitch));
  const ay = cmd.aimYaw;
  const ap = cmd.aimPitch;
  if (ay !== undefined && ap !== undefined && Number.isFinite(ay) && Number.isFinite(ap)) {
    const yaw = Math.fround(wrapAngle(Math.fround(ay)));
    const pitch = clampPitch(Math.fround(ap));
    if (aimCos(s.yaw, s.pitch, yaw, pitch) >= AIM.cosMaxDeviation) {
      s.aimYaw = yaw;
      s.aimPitch = pitch;
      return;
    }
  }
  s.aimYaw = s.yaw;
  s.aimPitch = s.pitch;
}
/**
 * Tempo de voo do Salto em chão plano (F97): subida a `gravityUp` e descida a `gravityDown`. A
 * velocidade no plano que cobre `d` metros é `d / LEAP_FLIGHT`.
 */
export const LEAP_FLIGHT = LEAP.lift / MOVE.gravityUp + LEAP.lift / Math.sqrt(MOVE.gravityUp * MOVE.gravityDown);

/** Alcance do Salto com a carga inteira, em chão plano. */
export const LEAP_RANGE = LEAP.speed * LEAP_FLIGHT;

/** Faixa do ponto mirado do Salto (F97): o servidor limita a ela, e o cliente também, para prever igual. */
export const LEAP_AIM_LIMIT = 1100;

const leapScratch = { x: 0, z: 0 };

/**
 * Velocidade no plano da decolagem do Salto (F97): na direção do ponto mirado, o bastante para pousar
 * nele, até o alcance da carga (`leapPower`); sob os pés, salta no lugar. Sem ponto (ou não finito: só
 * nos testes e comandos locais; pela rede o campo sempre chega, 0 por padrão), para a frente com a carga toda. Igual no servidor e na predição (o ponto chega em float32).
 */
function leapVelocity(s: MoveState, cmd: MoveCommand): { x: number; z: number } {
  leapScratch.x = -dsin(s.yaw) * s.leapPower;
  leapScratch.z = -dcos(s.yaw) * s.leapPower;
  const tx = cmd.leapX;
  const tz = cmd.leapZ;
  if (tx === undefined || tz === undefined || !Number.isFinite(tx) || !Number.isFinite(tz)) return leapScratch;
  const dx = Math.fround(tx) - s.x;
  const dz = Math.fround(tz) - s.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  // Gravidade menor (Pé-de-vento) alonga o voo na mesma proporção: a velocidade cai junto.
  const speed = Math.min(s.leapPower, (d * s.gravityMul) / LEAP_FLIGHT);
  leapScratch.x = d > 0 ? (dx / d) * speed : 0;
  leapScratch.z = d > 0 ? (dz / d) * speed : 0;
  return leapScratch;
}

const gradientScratch = { x: 0, z: 0 };
const cylinderScratch: Cylinder[] = [];

const clampUnit = (v: number): number => (v > 1 ? 1 : v < -1 ? -1 : v);

/** Aproxima a velocidade horizontal de (tx, tz) com aceleração máxima `accel·dt`. */
function accelerateTowards(s: MoveState, tx: number, tz: number, accel: number, dt: number): void {
  const dx = tx - s.vx;
  const dz = tz - s.vz;
  const dist = Math.sqrt(dx * dx + dz * dz);
  const maxDelta = accel * dt;
  if (dist <= maxDelta || dist === 0) {
    s.vx = tx;
    s.vz = tz;
  } else {
    s.vx += (dx / dist) * maxDelta;
    s.vz += (dz / dist) * maxDelta;
  }
}

/** Velocidade horizontal (módulo). */
export const horizontalSpeed = (s: MoveState): number => Math.sqrt(s.vx * s.vx + s.vz * s.vz);

/**
 * Um sub-passo de física (dt = 1/60 s no jogo). Determinístico: só aritmética básica, `sqrt`,
 * e seno/cosseno de `@eldrun/shared`. Cliente (predição) e servidor (autoridade) chamam esta mesma
 * função com o mesmo comando e o mesmo mundo.
 */
/** Comando sem ação (caído): só olhar, a física segue. */
const downedCommand = (cmd: MoveCommand): MoveCommand => ({ ...cmd, moveX: 0, moveZ: 0, jump: false, sprint: false, crouch: false, use: false, dash: false, leap: false });

export function stepMovement(s: MoveState, input: MoveCommand, world: SimWorld, dt: number): void {
  const cmd = s.downed ? downedCommand(input) : input;
  if (s.downed) {
    // Nada do que estava pendente (salto no buffer, Rasteira, canal de comer) acontece caído.
    s.jumpBuffer = 0;
    s.slideTime = 0;
    s.dashTime = 0;
    s.leaping = false;
    s.useTime = 0;
  }
  // A entrada chega pela rede como float32; o cliente arredonda igual antes de prever.
  applyLook(s, cmd);
  // Travessura do Saci: frente vira trás e esquerda vira direita (servidor e predição iguais).
  const flip = s.inverted ? -1 : 1;
  // Eixo não finito vale parado (a mesma faixa não pega NaN, que viraria posição NaN).
  const moveX = (Number.isFinite(cmd.moveX) ? clampUnit(Math.round(cmd.moveX)) : 0) * flip;
  const moveZ = (Number.isFinite(cmd.moveZ) ? clampUnit(Math.round(cmd.moveZ)) : 0) * flip;

  // Timers.
  s.coyote = Math.max(0, s.coyote - dt);
  s.jumpBuffer = Math.max(0, s.jumpBuffer - dt);
  s.jumpCooldown = Math.max(0, s.jumpCooldown - dt);
  s.slideCooldown = Math.max(0, s.slideCooldown - dt);
  s.dashCooldown = Math.max(0, s.dashCooldown - dt);
  const dashPressed = cmd.dash === true && !s.dashHeld;
  s.dashHeld = cmd.dash === true;
  const leapPressed = cmd.leap === true && !s.leapHeld;
  s.leapHeld = cmd.leap === true;
  if (cmd.jump && !s.jumpHeld) s.jumpBuffer = MOVE.jumpBuffer;
  const crouchPressed = cmd.crouch && !s.crouchHeld;
  s.jumpHeld = cmd.jump;
  s.crouchHeld = cmd.crouch;

  // Direção desejada no plano: yaw 0 olha para −z.
  const sin = dsin(s.yaw);
  const cos = dcos(s.yaw);
  let wishX = -sin * moveZ + cos * moveX;
  let wishZ = -cos * moveZ - sin * moveX;
  const wishLen = Math.sqrt(wishX * wishX + wishZ * wishZ);
  if (wishLen > 0) {
    wishX /= wishLen;
    wishZ /= wishLen;
  }

  // Comer: canal com a ação mantida; cada mordida completa incrementa o contador.
  const eating = cmd.use && s.canEat;
  if (eating) {
    s.useTime += dt;
    if (s.useTime >= EAT.seconds) {
      s.useTime = 0;
      s.eatCount = (s.eatCount + 1) & 0xffff;
    }
  } else {
    s.useTime = 0;
  }
  // Correr exige Fôlego (e não estar exausto) e não combina com comer.
  const sprint = cmd.sprint && s.stamina > 0 && !s.exhausted && !eating;

  const groundY = groundAt(world, s.x, s.z, s.y).height;
  const depth = world.seaLevel - s.y;
  s.swimming = depth > MOVE.swimDepth && world.seaLevel - groundY > MOVE.swimDepth;

  const cost = s.swimming
    ? swimStep(s, cmd, sprint, eating, wishX, wishZ, world, dt)
    : landStep(s, cmd, sprint, eating, moveZ, crouchPressed, dashPressed, leapPressed, wishX, wishZ, wishLen, groundY, world, dt);

  // Integração.
  const prevX = s.x;
  const prevY = s.y;
  const prevZ = s.z;
  s.x += s.vx * dt;
  s.y += s.vy * dt;
  s.z += s.vz * dt;

  // Cilindros e caixas antes do terreno: o empurrão lateral é seguido da altura do chão no ponto final.
  // O teto suspenso com o centro sobre ele vem antes de tudo (pouso rápido não leva empurrão de pilar).
  resolveCeilings(s, prevY, world);
  resolveCylinders(s, world);
  resolveBoxes(s, prevY, world);
  resolveTerrain(s, prevX, prevZ, world);
  if (s.leaping && (s.grounded || s.swimming)) s.leaping = false;

  // Limites do mundo e queda para fora.
  if (s.x > MOVE.worldLimit) s.x = MOVE.worldLimit;
  if (s.x < -MOVE.worldLimit) s.x = -MOVE.worldLimit;
  if (s.z > MOVE.worldLimit) s.z = MOVE.worldLimit;
  if (s.z < -MOVE.worldLimit) s.z = -MOVE.worldLimit;
  if (s.y < MOVE.killY) {
    s.x = s.safeX;
    s.y = s.safeY;
    s.z = s.safeZ;
    s.vx = 0;
    s.vy = 0;
    s.vz = 0;
  }
  if (s.grounded && s.y >= world.seaLevel) {
    s.safeX = s.x;
    s.safeY = s.y;
    s.safeZ = s.z;
  }
  s.submerged = s.y + eyeHeight(s) < world.seaLevel;
  updateStamina(s, cost + (s.submerged ? STAMINA.divePerSecond * dt : 0), dt);
  roundState(s);
}

/** Gasta ou recupera Fôlego (recupera só depois de `regenDelay` sem gastar, no chão ou à tona). */
function updateStamina(s: MoveState, cost: number, dt: number): void {
  if (s.exhausted && s.stamina >= s.staminaMax * STAMINA.exhaustedUntil) s.exhausted = false;
  if (cost > 0) {
    s.stamina = Math.max(0, s.stamina - cost);
    s.staminaIdle = 0;
    if (s.stamina <= 0) s.exhausted = true;
    return;
  }
  s.staminaIdle = Math.min(STAMINA.regenDelay, s.staminaIdle + dt);
  if (s.staminaIdle < STAMINA.regenDelay || s.stamina >= s.staminaMax || s.submerged) return;
  if (!s.grounded && !s.swimming) return;
  const factor = (s.swimming ? STAMINA.waterRegenFactor : 1) * (s.starving ? STAMINA.starvingRegenFactor : 1) * s.staminaRegenMul;
  s.stamina = Math.min(s.staminaMax, s.stamina + STAMINA.regenPerSecond * factor * dt);
}

/** Passo em terra. Devolve o Fôlego gasto neste passo. */
function landStep(
  s: MoveState,
  cmd: MoveCommand,
  sprint: boolean,
  eating: boolean,
  moveZ: number,
  crouchPressed: boolean,
  dashPressed: boolean,
  leapPressed: boolean,
  wishX: number,
  wishZ: number,
  wishLen: number,
  groundY: number,
  world: SimWorld,
  dt: number,
): number {
  let cost = 0;
  const speed = horizontalSpeed(s);
  const sprinting = sprint && moveZ > 0 && !cmd.crouch;

  // Investida (F66): no chão, para onde se olha, a `dashPower` m/s por `DASH.duration` s; a recarga
  // é a da habilidade. Só quem a tem (o servidor põe `dashPower`); servidor e predição iguais.
  if (dashPressed && s.dashPower > 0 && s.grounded && s.dashCooldown <= 0 && s.dashTime <= 0) {
    s.vx = -dsin(s.yaw) * s.dashPower;
    s.vz = -dcos(s.yaw) * s.dashPower;
    s.dashTime = DASH.duration;
    s.dashCooldown = DASH.cooldown;
    s.slideTime = 0;
  }

  // Salto do Falcão (F67): do chão, em arco, até o ponto mirado (F97); gasta a carga na hora (o servidor
  // zera a dele no mesmo passo). O pouso é quando volta ao chão (ou cai na água).
  if (leapPressed && s.leapPower > 0 && s.grounded && !s.leaping && s.dashTime <= 0) {
    const leap = leapVelocity(s, cmd);
    s.vx = leap.x;
    s.vz = leap.z;
    s.vy = LEAP.lift;
    s.grounded = false;
    s.coyote = 0;
    s.jumpBuffer = 0;
    s.slideTime = 0;
    s.leaping = true;
    s.leapPower = 0;
  }

  // Rasteira: agachar correndo, no chão e rápido o bastante.
  // Cada ação exige o custo inteiro em Fôlego (doc 02 §2–§3): abaixo dele, não sai.
  if (crouchPressed && s.grounded && sprint && speed >= MOVE.slideMinSpeed && s.slideCooldown <= 0 && s.slideTime <= 0 && s.stamina >= STAMINA.slide) {
    cost += STAMINA.slide;
    const boosted = Math.min(MOVE.slideMaxSpeed, speed + MOVE.slideBoost);
    s.vx = (s.vx / speed) * boosted;
    s.vz = (s.vz / speed) * boosted;
    s.slideTime = MOVE.slideDuration;
    s.slideCooldown = MOVE.slideCooldown + MOVE.slideDuration;
  }
  s.crouching = cmd.crouch || s.slideTime > 0;

  const shallow = world.seaLevel - groundY > 0 ? MOVE.shallowWaterFactor : 1;
  // Bônus de velocidade (Guaraná, Couro) nunca passam de 12 m/s (doc 02, Simpatias).
  let target = Math.min(MOVE.speedCap, (s.crouching ? MOVE.crouchSpeed : sprinting ? MOVE.sprintSpeed * s.sprintMul : MOVE.walkSpeed) * s.speedMul) * shallow * (eating ? EAT.speedFactor : 1);
  if (sprinting && s.grounded && wishLen > 0 && s.slideTime <= 0) cost += STAMINA.sprintPerSecond * dt;

  if (s.slideTime > 0 && s.grounded) {
    // Encosta não caminhável encerra a Rasteira (não se sobe morro íngreme deslizando).
    const g = world.gradient(s.x, s.z, gradientScratch);
    if (Math.sqrt(g.x * g.x + g.z * g.z) > MOVE.walkableSlope) s.slideTime = 0;
  }

  if (s.dashTime > 0) {
    // No avanço, a velocidade fica (sem frear nem obedecer ao direcional); no fim, cai para a de
    // caminhada (sem deslizar metros a 26 m/s).
    s.dashTime = Math.max(0, s.dashTime - dt);
    if (s.dashTime <= 0) {
      const v = horizontalSpeed(s);
      if (v > MOVE.walkSpeed) {
        s.vx = (s.vx / v) * MOVE.walkSpeed;
        s.vz = (s.vz / v) * MOVE.walkSpeed;
      }
    }
  } else if (s.slideTime > 0) {
    s.slideTime = Math.max(0, s.slideTime - dt);
    const v = horizontalSpeed(s);
    const next = Math.max(0, v - MOVE.slideDecel * dt);
    if (v > 0) {
      s.vx = (s.vx / v) * next;
      s.vz = (s.vz / v) * next;
    }
    if (next < MOVE.slideEndSpeed) s.slideTime = 0;
  } else if (s.grounded) {
    const g = world.gradient(s.x, s.z, gradientScratch);
    const slope = Math.sqrt(g.x * g.x + g.z * g.z);
    let control = 1;
    if (slope > MOVE.walkableSlope) {
      // Encosta íngreme: escorrega morro abaixo e controla pouco.
      control = MOVE.airControl;
      const sinTheta = slope / Math.sqrt(1 + slope * slope);
      const pull = MOVE.gravityDown * s.gravityMul * sinTheta * dt;
      s.vx -= (g.x / slope) * pull;
      s.vz -= (g.z / slope) * pull;
    }
    if (wishLen > 0) {
      const opposing = s.vx * wishX + s.vz * wishZ < 0;
      accelerateTowards(s, wishX * target, wishZ * target, (opposing ? MOVE.groundBrake : MOVE.groundAccel) * control, dt);
    } else if (control === 1) {
      const keep = Math.max(0, 1 - MOVE.groundFriction * dt);
      s.vx *= keep;
      s.vz *= keep;
    }
  } else if (wishLen > 0 && !s.leaping) {
    // No ar: controle parcial, sem frear além da velocidade atual. No Salto do Falcão, nenhum (F97: o
    // arco vai ao ponto mirado; correr ou desviar no ar o tiraria de lá).
    target = Math.max(target, horizontalSpeed(s));
    accelerateTowards(s, wishX * target, wishZ * target, MOVE.groundAccel * MOVE.airControl, dt);
  }

  // Salto (com coyote time, buffer e recarga) e saltos aéreos; abaixo do custo não salta.
  const left = s.stamina - cost;
  if (s.jumpBuffer > 0 && s.jumpCooldown <= 0) {
    if ((s.grounded || s.coyote > 0) && left >= STAMINA.jump) {
      cost += STAMINA.jump;
      s.vy = MOVE.jumpSpeed;
      s.grounded = false;
      s.coyote = 0;
      s.jumpBuffer = 0;
      s.jumpCooldown = MOVE.jumpCooldown;
      s.airJumps = s.maxAirJumps;
      s.slideTime = 0;
    } else if (!s.grounded && s.coyote <= 0 && s.airJumps > 0 && left >= STAMINA.airJump) {
      cost += STAMINA.airJump;
      s.vy = MOVE.airJumpSpeed;
      s.airJumps -= 1;
      s.jumpBuffer = 0;
      s.jumpCooldown = MOVE.jumpCooldown;
    }
  }

  if (!s.grounded) {
    s.vy -= (s.vy > 0 ? MOVE.gravityUp : MOVE.gravityDown) * s.gravityMul * dt;
    if (s.vy < -MOVE.terminalVelocity) s.vy = -MOVE.terminalVelocity;
  }
  return cost;
}

/** Passo na água. Devolve o Fôlego gasto pelo nado acelerado (o mergulho é cobrado à parte). */
function swimStep(s: MoveState, cmd: MoveCommand, sprint: boolean, eating: boolean, wishX: number, wishZ: number, world: SimWorld, dt: number): number {
  s.grounded = false;
  s.crouching = false;
  s.slideTime = 0;
  const fast = sprint && (wishX !== 0 || wishZ !== 0);
  const target = (fast ? MOVE.swimSprintSpeed : MOVE.swimSpeed) * s.speedMul * (eating ? EAT.speedFactor : 1);
  accelerateTowards(s, wishX * target, wishZ * target, MOVE.swimAccel, dt);
  // Flutuação: sobe com salto, afunda agachando, senão volta à superfície.
  const surface = world.seaLevel - MOVE.swimDepth;
  let targetVy: number;
  if (cmd.jump) targetVy = MOVE.swimVertical;
  else if (cmd.crouch) targetVy = -MOVE.swimVertical;
  else targetVy = Math.max(-MOVE.swimVertical, Math.min(MOVE.swimVertical, (surface - s.y) * 2));
  const dv = targetVy - s.vy;
  const maxDv = MOVE.swimAccel * dt;
  s.vy += dv > maxDv ? maxDv : dv < -maxDv ? -maxDv : dv;
  // Saindo da água pelo salto na borda.
  if (cmd.jump && s.y > surface - 0.05) s.vy = Math.max(s.vy, MOVE.swimVertical);
  return fast ? STAMINA.swimSprintPerSecond * dt : 0;
}

function resolveTerrain(s: MoveState, prevX: number, prevZ: number, world: SimWorld): void {
  const at = groundAt(world, s.x, s.z, s.y);
  let ground = at.height;
  const wasGrounded = s.grounded;
  const prevGround = groundAt(world, prevX, prevZ, s.y).height;
  // Parede ou subida contínua em superfície não caminhável: bloqueia o avanço horizontal.
  // (Desnível acima do degrau, ou, em contato com o chão, qualquer subida numa encosta íngreme.)
  // Em cima de construção (piso, rampa), o chão é sempre caminhável.
  const rise = ground - prevGround;
  if (!at.onBox && (ground - s.y > MOVE.stepHeight || (wasGrounded && rise > 0))) {
    const g = world.gradient(s.x, s.z, gradientScratch);
    const slope = Math.sqrt(g.x * g.x + g.z * g.z);
    if (slope > MOVE.walkableSlope) {
      s.x = prevX;
      s.z = prevZ;
      s.vx = 0;
      s.vz = 0;
      ground = prevGround;
    }
  }
  if (s.y <= ground) {
    s.y = ground;
    if (s.vy < 0) s.vy = 0;
    s.grounded = true;
  } else if (wasGrounded && s.vy <= 0 && s.y - ground <= MOVE.snapDown && !s.swimming) {
    s.y = ground;
    s.vy = 0;
    s.grounded = true;
  } else {
    if (wasGrounded) s.coyote = MOVE.coyoteTime;
    s.grounded = false;
  }
}

function resolveCylinders(s: MoveState, world: SimWorld): void {
  const near = world.cylinders(s.x, s.z, MOVE.radius + 2, cylinderScratch);
  for (const c of near) {
    if (s.y >= c.top || s.y + MOVE.height <= c.bottom) continue;
    const dx = s.x - c.x;
    const dz = s.z - c.z;
    const minDist = c.radius + MOVE.radius;
    const d2 = dx * dx + dz * dz;
    if (d2 >= minDist * minDist) continue;
    const d = Math.sqrt(d2);
    // Centro coincidente: empurra numa direção fixa (determinística).
    const nx = d > 1e-6 ? dx / d : 1;
    const nz = d > 1e-6 ? dz / d : 0;
    s.x = c.x + nx * minDist;
    s.z = c.z + nz * minDist;
    const into = s.vx * nx + s.vz * nz;
    if (into < 0) {
      s.vx -= into * nx;
      s.vz -= into * nz;
    }
  }
  near.length = 0;
}

/**
 * Teto suspenso (`ceiling`: a laje da Caverna, em faixas finas) com o centro do corpo sobre ele e na
 * altura dele: sai por cima se vinha de cima ou o pé passou do meio da espessura, senão por baixo. Nunca
 * de lado: pela face mais rasa (a fina), o corpo pulava de faixa em faixa, metros num sub-passo.
 */
function exitCeiling(s: MoveState, prevY: number, b: Box, head: number): void {
  if (prevY >= b.maxY - 1e-6 || s.y >= (b.minY + b.maxY) / 2) {
    s.y = b.maxY;
    if (s.vy < 0) s.vy = 0;
  } else {
    s.y = b.minY - head;
    if (s.vy > 0) s.vy = 0;
  }
}

/** Teto suspenso com o centro do corpo sobre ele, na altura dele: sai na vertical (`exitCeiling`). */
function resolveCeilings(s: MoveState, prevY: number, world: SimWorld): void {
  if (!world.boxes) return;
  const head = s.crouching ? MOVE.crouchHeight : MOVE.height;
  boxScratch.length = 0;
  for (const b of world.boxes(s.x, s.z, 0, boxScratch)) {
    if (!b.ceiling || b.minY >= s.y + head || b.maxY <= s.y) continue;
    if (s.x >= b.minX && s.x <= b.maxX && s.z >= b.minZ && s.z <= b.maxZ) exitCeiling(s, prevY, b, head);
  }
  boxScratch.length = 0;
}

/**
 * Caixas das construções: o que está acima de um degrau é sólido. Bater a cabeça numa laje
 * subindo zera a velocidade vertical; de lado, o corpo (círculo) sai da caixa pelo menor caminho.
 * No teto suspenso, nunca de lado: acima dos joelhos é cabeçada (subindo ou não), e o centro sobre ele
 * sai na vertical (`exitCeiling`).
 */
function resolveBoxes(s: MoveState, prevY: number, world: SimWorld): void {
  if (!world.boxes) return;
  const head = s.crouching ? MOVE.crouchHeight : MOVE.height;
  const r = MOVE.radius;
  boxScratch.length = 0;
  for (const b of world.boxes(s.x, s.z, r + 1, boxScratch)) {
    // Fora da faixa vertical do corpo.
    if (b.minY >= s.y + head || b.maxY <= s.y) continue;
    const cx = s.x < b.minX ? b.minX : s.x > b.maxX ? b.maxX : s.x;
    const cz = s.z < b.minZ ? b.minZ : s.z > b.maxZ ? b.maxZ : s.z;
    const dx = s.x - cx;
    const dz = s.z - cz;
    const d2 = dx * dx + dz * dz;
    if (d2 >= r * r) continue;
    // Um empurrão de antes pôs o centro sobre uma faixa do teto: sai na vertical, como acima.
    if (b.ceiling && d2 === 0) {
      exitCeiling(s, prevY, b, head);
      continue;
    }
    const top = boxTopAt(b, cx, cz);
    // Degrau (ou rampa por baixo do pé): o chão resolve.
    if (top <= s.y + MOVE.stepHeight) continue;
    // Caindo rápido, o pé passou do topo neste passo (estava em cima antes) com o centro sobre a
    // caixa: pousa nela, em vez de tratar a laje como parede e empurrar para o lado.
    if (s.vy <= 0 && prevY >= top - 1e-6 && d2 === 0) {
      s.y = top;
      s.vy = 0;
      continue;
    }
    // Laje acima dos joelhos ao subir (ou teto suspenso, ao se levantar embaixo da beira): bate a cabeça.
    if ((s.vy > 0 || b.ceiling) && b.minY > s.y + MOVE.stepHeight) {
      s.y = b.minY - head;
      if (s.vy > 0) s.vy = 0;
      continue;
    }
    let nx: number;
    let nz: number;
    let push: number;
    if (d2 > 1e-12) {
      const d = Math.sqrt(d2);
      nx = dx / d;
      nz = dz / d;
      push = r - d;
    } else {
      // Centro dentro da caixa: sai pelo lado de menor penetração (ordem fixa nos empates).
      const left = s.x - b.minX;
      const right = b.maxX - s.x;
      const back = s.z - b.minZ;
      const front = b.maxZ - s.z;
      const m = Math.min(left, right, back, front);
      if (m === left) [nx, nz, push] = [-1, 0, left + r];
      else if (m === right) [nx, nz, push] = [1, 0, right + r];
      else if (m === back) [nx, nz, push] = [0, -1, back + r];
      else [nx, nz, push] = [0, 1, front + r];
    }
    s.x += nx * push;
    s.z += nz * push;
    const into = s.vx * nx + s.vz * nz;
    if (into < 0) {
      s.vx -= into * nx;
      s.vz -= into * nz;
    }
  }
  boxScratch.length = 0;
}

/** Executa um tick de input com `subSteps` sub-passos (a mesma divisão no cliente e no servidor). */
export function stepTick(s: MoveState, cmd: MoveCommand, world: SimWorld, stepSeconds: number, subSteps: number): void {
  // Na travessia, a Jangada leva o corpo: sem física nem comando (o servidor move; a predição espera).
  if (s.carried) {
    s.vx = 0;
    s.vy = 0;
    s.vz = 0;
    applyLook(s, cmd);
    return;
  }
  const dt = stepSeconds / subSteps;
  for (let i = 0; i < subSteps; i++) stepMovement(s, cmd, world, dt);
}

/** Altura do olho conforme a postura. */
export const eyeHeight = (s: Pick<MoveState, "crouching">): number => (s.crouching ? MOVE.crouchEyeHeight : MOVE.eyeHeight);

/** Chão plano em y = 0, sem mar nem obstáculos (para medir o salto). */
const FLAT: SimWorld = {
  seaLevel: -Infinity,
  height: () => 0,
  gradient: (_x, _z, out) => {
    out.x = 0;
    out.z = 0;
    return out;
  },
  cylinders: (_x, _z, _r, out) => out,
};

/**
 * Quanto o pé sobe num salto parado em chão plano, medido com o próprio passo da simulação
 * (`stepMovement` a `SUB_STEP_SECONDS`): ≈ 1,18 m, abaixo de v²/2g (≈ 1,24 m) porque cada sub-passo
 * aplica a gravidade antes de mover. Sem Mandinga (gravidade normal) nem saltos aéreos.
 */
export function jumpApex(): number {
  const still: MoveCommand = { moveX: 0, moveZ: 0, yaw: 0, pitch: 0, jump: false, sprint: false, crouch: false, use: false };
  const s = createMoveState();
  stepMovement(s, still, FLAT, SUB_STEP_SECONDS);
  stepMovement(s, { ...still, jump: true }, FLAT, SUB_STEP_SECONDS);
  let apex = 0;
  while (s.y > apex) {
    apex = s.y;
    stepMovement(s, still, FLAT, SUB_STEP_SECONDS);
  }
  return apex;
}
